import { ActuComments } from '../ActuComments/ActuComments.component';
import { LastAndMostViewedContentWithAds } from './components/LastAndMostViewedContentWithAds.compoment';
import { ActuContext } from 'pages/actualite/[slug]';
import React, { ComponentPropsWithRef, FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ADVERT_FORMAT_ID, ADVERT_PAGE_ID } from '@adverts/business/Adverts.contants';
import { useAdvert } from '@adverts/business/useAdvert';
import { GenericAd } from '@adverts/components/GenericAd.component';
import { ContentApi } from '@api/business/api.utils';
import { BreadCrumbStep } from '@components/BreadCrumb/BreadCrumb.types';
import { ClippedImage } from '@components/ClippedImage/ClippedImage.component';
import { ContentAuthor } from '@components/content/ContentAuthor/ContentAuthor.component';
import { ContentBody } from '@components/content/ContentBody/ContentBody.component';
import { ContentFooter } from '@components/content/ContentFooter/ContentFooter.component';
import { ContentLayout, ContentLayoutLoaderOrError } from '@components/content/ContentLayout/ContentLayout.component';
import { ContentStat } from '@components/content/ContentStat/ContentStat.component';
import { ContentSubtitle } from '@components/content/ContentSubtitle/ContentSubtitle.component';
import { ProTag, ProTagVariant } from '@components/ProTag/ProTag.component';
import ReadAlsoArticle from '@components/ReadAlso/ReadAlsoArticle.component';
import { Text } from '@components/Text/Text.component';
import { scrollToElement } from '@layout/business/Layout.utils';
import { useTranslation } from '@locales/useTranslation.hook';
import { Breakpoint, useClassNames, useIsBreakpointDown } from '@styles/breakpoints';
import { userSelectors } from '@user/business/User.store';
import { formatDateToIso, formatDateWithDay } from '@utils/date';
import { ClippedImageStyle } from '@utils/images';
import { urlEnum } from '@utils/url';
import classnames from 'classnames';
import { ATLevel2, PlcTagProps } from 'features/tagPlan/business/TagPlan.types';
import styles from './ActuLayout.module.scss';

const ARTICLE_BREAKPOINT = 'm';

const ActuLayout: FC<ComponentPropsWithRef<'div'>> = ({ className, ...rest }) => {
  const { t } = useTranslation(['actu']);
  const isMobile = useIsBreakpointDown(Breakpoint.m);
  const {
    article,
    nbComments,
    commentsRef,
    readAlso,
    isValidating,
    isPreview: { data: isPreview },
  } = useContext(ActuContext);
  const [nbViews, setNbViews] = useState<number | undefined>();
  const isViewsIncreased = useRef<string>();
  const userIsPro = useSelector(userSelectors.isPro);

  useAdvert({
    pageId: ADVERT_PAGE_ID.Actu,
  });

  const rootClassName = classnames(styles.actu, className);

  const actuStatsClassNames = useClassNames({
    all: ['actu-stats', 'flex', 'align-center', nbComments.initialData ? 'with-border' : undefined, 'plc-mt-s'],
    m: ['plc-mt-0', 'plc-ml-m'],
  });

  async function patchViews(id: number) {
    const newNbViews = await ContentApi.getViewCount(id)
      .then((r) => r.data)
      .catch(() => undefined);

    setNbViews(newNbViews);
  }

  useEffect(() => {
    if (article.initialData && article.initialData.id && isViewsIncreased.current !== article.initialData.slug) {
      patchViews(article.initialData.id);
      isViewsIncreased.current = article.initialData.slug;
    }
  }, [article.initialData, isViewsIncreased.current]);

  const tagProps: PlcTagProps = useMemo(
    () => ({
      isDataLoading: isValidating.initialData,
      level2: ATLevel2.Actu,
      tagPageNameI18nArray: [
        'actu.tag.title',
        article.initialData ? { code: article.initialData.slug, notI18n: true } : undefined,
      ],
    }),
    [isValidating.initialData, article.initialData]
  );

  if (!article.initialData) {
    return (
      <ContentLayoutLoaderOrError
        className={rootClassName}
        isLoading={isValidating.initialData}
        withIllu={true}
        {...rest}
      />
    );
  }

  const {
    isPro: articleIsPro,
    metaTitle,
    metaDescription,
    title,
    body,
    author,
    interestCodes = [],
    activityCodes = [],
    featuredMedia,
    featuredMediaMobile,
    subtitle,
    publishDate,
    signature,
    canonicalLink,
    slug,
  } = article.initialData;

  const steps: BreadCrumbStep[] = [
    {
      label: 'common.newspaper-short',
      linkProps: { as: urlEnum.home, href: urlEnum.home },
    },
    {
      label: title,
      notI18n: true,
    },
  ];

  function scrollToComments() {
    scrollToElement(commentsRef.initialData);
  }

  const bodyIsPartial = articleIsPro && !userIsPro && !isPreview;

  const areCommentsEnabled = article.initialData.enableComments !== false;
  return (
    <>
      <GenericAd formatId={isMobile ? ADVERT_FORMAT_ID.MegaBanner : ADVERT_FORMAT_ID.Arch} />
      <ContentLayout
        articleTitle={title}
        articleUrl={`${urlEnum.actu}/${slug}`}
        breadcrumbSteps={steps}
        className={rootClassName}
        description={subtitle}
        gridBottomComponent={areCommentsEnabled ? <ActuComments /> : undefined}
        gridReadAlsoArticle={readAlso && <ReadAlsoArticle {...readAlso} />}
        gridRightComponent={<LastAndMostViewedContentWithAds slug={article.initialData.slug} />}
        illustrationComponent={
          isMobile
            ? featuredMediaMobile && (
                <ClippedImage
                  alt={featuredMediaMobile.alt}
                  caption={featuredMediaMobile.alt}
                  clipStyle={ClippedImageStyle.default}
                  plcSrc={{ src: featuredMediaMobile.url, type: 'external' }}
                />
              )
            : featuredMedia && (
                <ClippedImage
                  alt={featuredMedia.alt}
                  caption={featuredMedia.alt}
                  clipStyle={ClippedImageStyle.default}
                  plcSrc={{ src: featuredMedia.url, type: 'external' }}
                />
              )
        }
        imgAlt={featuredMedia?.alt}
        imgUrl={featuredMedia?.url}
        metas={{
          canonical: canonicalLink,
          metaDescI18n: metaDescription || subtitle,
          metaTitleI18n: metaTitle || title,
        }}
        postTitleComponent={
          <>
            {author && <ContentAuthor author={author} />}
            <div className={actuStatsClassNames}>
              <ContentStat className="plc-mr-m" statCount={nbViews} type="view" />
              {areCommentsEnabled && (
                <ContentStat onClick={scrollToComments} statCount={nbComments.data} type="comment" />
              )}
            </div>
          </>
        }
        preTitleComponent={
          <div>
            <Text
              className={styles['article-publish-date']}
              dateTime={formatDateToIso(publishDate)}
              tag="time"
              variant="h6"
            >
              {formatDateWithDay(publishDate)}
            </Text>
            {articleIsPro && <ProTag className="plc-my-xxs plc-my-up-s-0" variant={ProTagVariant.long} />}
          </div>
        }
        tagProps={tagProps}
        {...rest}
      >
        <ContentSubtitle className={`plc-mb-up-${ARTICLE_BREAKPOINT}-l`} isArticle subtitle={subtitle} />
        <ContentBody
          body={body}
          className={`plc-mb-l plc-mb-up-${ARTICLE_BREAKPOINT}-xl`}
          isArticle
          isPartial={bodyIsPartial}
        />
        <ContentFooter
          activityCodes={activityCodes}
          className={`plc-mb-l plc-mb-up-${ARTICLE_BREAKPOINT}-xl`}
          interestCodes={interestCodes}
          isTagsDarkGreenStyle
          signature={signature ? t('actu.author-signature', { signature }) : undefined}
        />
      </ContentLayout>
    </>
  );
};

export { ActuLayout };
